import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Минимальные типы Web Speech API — в lib.dom.d.ts они есть не во всех конфигурациях TS.
interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string };
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}

interface SpeechRecognitionErrorEventLike {
  error: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

const getRecognitionCtor = (): SpeechRecognitionCtor | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

export interface UseSpeechRecognitionOptions {
  /** Промежуточный (ещё не финализированный) результат — для живого предпросмотра. */
  onInterim?: (transcript: string) => void;
  /** Финальный распознанный фрагмент. */
  onFinal?: (transcript: string) => void;
  /** Ошибка распознавания (not-allowed, no-speech, network и т.п.). */
  onError?: (error: string) => void;
  /** Язык распознавания. По умолчанию ru-RU. */
  lang?: string;
}

export interface UseSpeechRecognitionResult {
  isSupported: boolean;
  isRecording: boolean;
  start: () => void;
  stop: () => void;
}

/**
 * Голосовой ввод на базе Web Speech API (вариант A).
 * Распознавание выполняется средствами браузера; в Chrome аудио обрабатывается на серверах Google.
 */
export const useSpeechRecognition = ({
  onInterim,
  onFinal,
  onError,
  lang = 'ru-RU',
}: UseSpeechRecognitionOptions = {}): UseSpeechRecognitionResult => {
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const [isRecording, setIsRecording] = useState(false);

  // Колбэки держим в ref, чтобы не пересоздавать распознаватель на каждый рендер.
  const onInterimRef = useRef(onInterim);
  const onFinalRef = useRef(onFinal);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onInterimRef.current = onInterim;
    onFinalRef.current = onFinal;
    onErrorRef.current = onError;
  }, [onInterim, onFinal, onError]);

  const isSupported = useMemo(() => getRecognitionCtor() !== null, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    if (recognitionRef.current) return; // запись уже идёт
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;

    const recognition = new Ctor();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      if (finalTranscript) onFinalRef.current?.(finalTranscript);
      if (interimTranscript) onInterimRef.current?.(interimTranscript);
    };

    recognition.onerror = (event) => {
      onErrorRef.current?.(event.error);
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setIsRecording(false);
    };

    recognitionRef.current = recognition;
    setIsRecording(true);
    recognition.start();
  }, [lang]);

  // Останавливаем распознавание при размонтировании.
  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, []);

  return { isSupported, isRecording, start, stop };
};
